import { UiState } from '../state/UiState';
import { LocalStorageUiShell } from '../infrastructure/LocalStorageUiShell';
import { t } from '../i18n/i18n';

export interface PresetModalsCallbacks {
  onPresetApplied: (presetId: string, name: string) => void;
  onTriggerPoke?: () => void;
  onUpgradeRequested?: (feature: string) => void;
}

export class PresetModals {
  private uiState: UiState;
  private uiPort: LocalStorageUiShell;
  private callbacks: PresetModalsCallbacks;

  private saveModalEl: HTMLElement;
  private managerModalEl: HTMLElement;

  private saveTitleEl!: HTMLElement;
  private nameLabelEl!: HTMLElement;
  private previewRadiusEl!: HTMLElement;
  private previewStiffEl!: HTMLElement;
  private previewDampEl!: HTMLElement;
  private inputPresetNameEl!: HTMLInputElement;
  private webNoticeEl!: HTMLElement;
  private btnCancelSaveEl!: HTMLButtonElement;
  private btnConfirmSaveEl!: HTMLButtonElement;

  private managerTitleEl!: HTMLElement;
  private presetCardsListEl!: HTMLElement;
  private btnManagerCloseEl!: HTMLButtonElement;

  constructor(
    container: HTMLElement,
    uiState: UiState,
    uiPort: LocalStorageUiShell,
    callbacks: PresetModalsCallbacks
  ) {
    this.uiState = uiState;
    this.uiPort = uiPort;
    this.callbacks = callbacks;

    this.saveModalEl = document.createElement('div');
    this.saveModalEl.className = 'modal-backdrop';
    this.saveModalEl.id = 'modalSavePreset';

    this.managerModalEl = document.createElement('div');
    this.managerModalEl.className = 'modal-backdrop';
    this.managerModalEl.id = 'modalPresetManager';

    container.appendChild(this.saveModalEl);
    container.appendChild(this.managerModalEl);

    this.renderSaveModal();
    this.renderManagerModal();
    this.bindEvents();

    this.uiState.subscribe(() => {
      this.updateTexts();
    });
  }

  // --- 保存モーダル ---
  private renderSaveModal(): void {
    const texts = t();

    this.saveModalEl.innerHTML = `
      <div class="modal-card">
        <div class="modal-header">
          <div class="modal-title">
            <span>💾</span>
            <span id="savePresetModalTitle">${texts.preset.saveTitle}</span>
          </div>
          <button class="modal-close" id="btnCloseSaveModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="control-group">
            <label id="savePresetNameLabel" style="font-weight:600; display:block; margin-bottom:6px;">${texts.preset.nameLabel}</label>
            <input type="text" id="inputPresetName" class="form-input" value="${texts.preset.defaultName}" style="width:100%; padding:8px 12px; border-radius:var(--radius-sm); border:1px solid var(--color-card-border); font-family:inherit;">
          </div>
          <div style="background:#faf8f5; border:1px solid var(--color-card-border); border-radius:var(--radius-sm); padding:12px; margin-bottom:14px; font-size:12px;">
            <div style="font-weight:600; margin-bottom:6px;">Parameters:</div>
            <div>・${texts.physics.radius}: <span id="previewRadius">12%</span></div>
            <div>・${texts.physics.stiffness}: <span id="previewStiff">50%</span></div>
            <div>・${texts.physics.damping}: <span id="previewDamp">75%</span></div>
          </div>
          <div class="web-overwrite-notice" id="webOverwriteNotice" style="background:#fff3cd; color:#856404; padding:8px 12px; border-radius:var(--radius-sm); font-size:11px; margin-bottom:12px; border:1px solid #ffeeba;">
            ${texts.preset.maxLimitNotice}
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" id="btnCancelSave">${texts.preset.cancelBtn}</button>
          <button class="btn btn-primary" id="btnConfirmSave">${texts.preset.saveBtn}</button>
        </div>
      </div>
    `;

    this.saveTitleEl = this.saveModalEl.querySelector('#savePresetModalTitle') as HTMLElement;
    this.nameLabelEl = this.saveModalEl.querySelector('#savePresetNameLabel') as HTMLElement;
    this.previewRadiusEl = this.saveModalEl.querySelector('#previewRadius') as HTMLElement;
    this.previewStiffEl = this.saveModalEl.querySelector('#previewStiff') as HTMLElement;
    this.previewDampEl = this.saveModalEl.querySelector('#previewDamp') as HTMLElement;
    this.inputPresetNameEl = this.saveModalEl.querySelector('#inputPresetName') as HTMLInputElement;
    this.webNoticeEl = this.saveModalEl.querySelector('#webOverwriteNotice') as HTMLElement;
    this.btnCancelSaveEl = this.saveModalEl.querySelector('#btnCancelSave') as HTMLButtonElement;
    this.btnConfirmSaveEl = this.saveModalEl.querySelector('#btnConfirmSave') as HTMLButtonElement;
  }

  // --- 管理一覧モーダル ---
  private renderManagerModal(): void {
    const texts = t().preset;

    this.managerModalEl.innerHTML = `
      <div class="modal-card">
        <div class="modal-header">
          <div class="modal-title">
            <span>📚</span>
            <span id="managerModalTitle">${texts.manageTitle}</span>
          </div>
          <button class="modal-close" id="btnCloseManagerModal">&times;</button>
        </div>
        <div class="modal-body">
          <div id="presetCardsList" style="display:flex; flex-direction:column; gap:8px;"></div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary" id="btnManagerClose" style="width:100%;">${texts.cancelBtn}</button>
        </div>
      </div>
    `;

    this.managerTitleEl = this.managerModalEl.querySelector('#managerModalTitle') as HTMLElement;
    this.presetCardsListEl = this.managerModalEl.querySelector('#presetCardsList') as HTMLElement;
    this.btnManagerCloseEl = this.managerModalEl.querySelector('#btnManagerClose') as HTMLButtonElement;
  }

  private updateTexts(): void {
    const texts = t();
    if (this.saveTitleEl) this.saveTitleEl.textContent = texts.preset.saveTitle;
    if (this.nameLabelEl) this.nameLabelEl.textContent = texts.preset.nameLabel;
    if (this.webNoticeEl) this.webNoticeEl.textContent = texts.preset.maxLimitNotice;
    if (this.btnCancelSaveEl) this.btnCancelSaveEl.textContent = texts.preset.cancelBtn;
    if (this.btnConfirmSaveEl) this.btnConfirmSaveEl.textContent = texts.preset.saveBtn;
    if (this.managerTitleEl) this.managerTitleEl.textContent = texts.preset.manageTitle;
    if (this.btnManagerCloseEl) this.btnManagerCloseEl.textContent = texts.preset.cancelBtn;
  }

  private bindEvents(): void {
    // 保存モーダル閉じる
    this.saveModalEl.querySelector('#btnCloseSaveModal')?.addEventListener('click', () => this.hideSaveModal());
    this.btnCancelSaveEl?.addEventListener('click', () => this.hideSaveModal());

    // 保存確定
    this.btnConfirmSaveEl?.addEventListener('click', async () => {
      const texts = t().preset;
      const name = this.inputPresetNameEl.value.trim() || texts.defaultName;
      const state = this.uiState.getSnapshot();

      const result = await this.uiPort.savePreset({
        name,
        assetId: 'current-image',
        params: {
          softness: state.physics.radiusPercent,
          stiffness: state.physics.stiffness,
          damping: state.physics.damping,
        },
      });

      if (result.success && result.presetId) {
        this.uiState.setPreset(result.presetId, name);
        this.hideSaveModal();
        this.callbacks.onPresetApplied(result.presetId, name);
        this.callbacks.onTriggerPoke?.();
      }
    });

    // 管理モーダル閉じる
    this.managerModalEl.querySelector('#btnCloseManagerModal')?.addEventListener('click', () => this.hideManagerModal());
    this.btnManagerCloseEl?.addEventListener('click', () => this.hideManagerModal());
  }

  openSaveModal(): void {
    const curTexts = t();
    if (!this.uiState.isPresetSaveAllowed()) {
      this.callbacks.onUpgradeRequested?.(curTexts.upgrade.perkPreset);
      return;
    }

    const state = this.uiState.getSnapshot();
    this.previewRadiusEl.textContent = state.physics.radiusSlider + '%';
    this.previewStiffEl.textContent = state.physics.stiffnessSlider + '%';
    this.previewDampEl.textContent = state.physics.dampingSlider + '%';
    this.inputPresetNameEl.value = curTexts.preset.defaultName;

    this.webNoticeEl.style.display = 'none';

    this.saveModalEl.classList.add('show');
  }

  hideSaveModal(): void {
    this.saveModalEl.classList.remove('show');
  }

  async openManagerModal(): Promise<void> {
    const texts = t().preset;
    const presets = await this.uiPort.listPresets();
    const currentId = this.uiState.getSnapshot().currentPresetId;
    this.presetCardsListEl.innerHTML = '';

    for (const p of presets) {
      const data = await this.uiPort.loadPreset(p.id);
      const isDefault = p.id === 'p_default';
      const isCurrent = p.id === currentId;

      const card = document.createElement('div');
      card.style.cssText = `
        display:flex; justify-content:space-between; align-items:center;
        padding:10px 14px; background:#faf8f5; border:1px solid ${isCurrent ? 'var(--color-primary)' : 'var(--color-card-border)'};
        border-radius:var(--radius-sm);
      `;

      card.innerHTML = `
        <div>
          <div style="font-weight:700; font-size:13px; display:flex; align-items:center; gap:6px;">
            ${p.name}
            ${isCurrent ? '<span style="font-size:10px; background:var(--color-primary); color:#fff; padding:1px 6px; border-radius:var(--radius-pill);">Active</span>' : ''}
          </div>
          <div style="font-size:11px; color:var(--color-text-muted); margin-top:2px;">
            ${data ? `${(data.params.softness * 100).toFixed(0)}% / ${(data.params.stiffness * 100).toFixed(0)}%` : ''}
          </div>
        </div>
        <div style="display:flex; gap:6px;">
          <button class="btn btn-secondary btn-apply" style="padding:4px 10px; font-size:12px;">${texts.applyBtn}</button>
          ${!isDefault ? `<button class="btn btn-secondary btn-delete" style="padding:4px 8px; font-size:12px;" title="${texts.deleteBtn}">🗑️</button>` : ''}
        </div>
      `;

      card.querySelector('.btn-apply')?.addEventListener('click', () => {
        if (data) {
          const rSlider = Math.round(data.params.softness * 100);
          const sSlider = Math.round(((data.params.stiffness - 0.05) / 0.20) * 100);
          const dSlider = Math.round(((data.params.damping - 0.82) / 0.14) * 100);
          this.uiState.setPhysicsSliders(rSlider, sSlider, dSlider);
        }
        this.uiState.setPreset(p.id, p.name);
        this.callbacks.onPresetApplied(p.id, p.name);
        this.callbacks.onTriggerPoke?.();
        this.hideManagerModal();
      });

      card.querySelector('.btn-delete')?.addEventListener('click', async () => {
        await this.uiPort.deletePreset(p.id);
        this.openManagerModal();
      });

      this.presetCardsListEl.appendChild(card);
    }

    this.managerModalEl.classList.add('show');
  }

  hideManagerModal(): void {
    this.managerModalEl.classList.remove('show');
  }
}
